import { useDispatch } from 'react-redux';

import { useIsMobile } from '@sravni/react-utils';
import { BUSINESS_OFFERS, COMMON_OFFERS, OTHER_OFFERS } from '@sravni/ugc/lib/specialOffers/lists';
import type { Tab, VITRINS_OFFERS_TYPE } from '@sravni/ugc/lib/specialOffers/types';

import { getAdvertisingAction, getAdvertisingLabel } from '../helpers/getDataFromOffer';
import { getTabsInfo } from '../helpers/getTabsInfo';
import { decorateLinks } from '../helpers/offerLinks/decorateLinks';
import type { EventPayload } from '../helpers/sendMessageToParentWindow';
import type { EnrichedOfferFromBff, Links } from '../interfaces/offers';
import type { IShortOrganization } from '../interfaces/organizations';
import { useAnalyticsParams, useIsAnalyticsParamsLoaded } from '../store/hooks/offers';
import { addPixel } from '../store/reducers/offers';
import type { ProductTermFromBff } from '../types/offers';
import { WIDGET_TYPE } from '../types/widgetType';

const EXCEPT_NAMES = [
  {
    name: 'Московский Кредитный Банк',
    shortName: 'МКБ',
  },
  {
    name: 'Азиатско-Тихоокеанский Банк',
    shortName: 'АТБ',
  },
];

type OfferDataParams = {
  tab: Tab<VITRINS_OFFERS_TYPE>;
  item: EnrichedOfferFromBff;
  isPopup: boolean;
  widgetType?: WIDGET_TYPE;
};

export type CommonOfferData = Links & {
  bankName: string;
  logoSrc: string;
  isCard: boolean;
  offerTitle: string;
  rating?: number;
  reviewCount?: number;
  conditions: ProductTermFromBff[];
  isPartnerCard: boolean;
  organization: IShortOrganization;
  noticeAboutAdvertisement?: string;
  tag?: string;
  buttonLabel?: string;
  clickBlockEvent: EventPayload;
  viewBlockEvent: EventPayload;
  offerTitleClickEvent?: EventPayload;
  buttonClickEvent?: EventPayload;
  sendViewingEventViaPixel?: () => void;
  sendClickEventViaPixel?: () => void;
  labelFromAdvertising?: string;
};

// eslint-disable-next-line max-statements
export const useOfferData = ({ tab, item, isPopup, widgetType }: OfferDataParams): CommonOfferData => {
  const isMobile = useIsMobile();
  const analyticsParams = useAnalyticsParams();
  const dispatch = useDispatch();
  const isAnalyticsParamsLoaded = useIsAnalyticsParamsLoaded();
  const isVerticalWidget = widgetType === WIDGET_TYPE.vertical;

  const { advertising, logoSrc, tag, isCard, organization, productName, conditions } = item;

  const {
    rating: orgRating,
    reviewCount: orgReviewCount,
    id: organizationId,
    isPartnerCard,
    name: organizationName,
  } = organization;

  const offerTitle = isCard ? productName : `«${productName}»`;

  const exceptName = EXCEPT_NAMES.find((elem) => elem.name === organizationName);
  const bankName = exceptName ? exceptName.shortName : organizationName;

  const tabInfo = getTabsInfo(tab);
  const { advertisingCategory, buttonAction } = tabInfo;

  const advertisingAction = getAdvertisingAction({ tab, advertising });
  const advertisingLabel = getAdvertisingLabel({
    organizationName,
    offerName: productName,
    isVerticalWidget,
    tab,
    offerId: item._id,
  });
  const advertisingValue = advertising?.cost;

  // let noticeAboutAdvertisement = `Реклама. ${organization?.name.full ?? organization?.name.short}`;
  let noticeAboutAdvertisement = null;

  if (tab.type === OTHER_OFFERS.bukmekery) {
    noticeAboutAdvertisement = null;
  }

  const clickBlockEvent = {
    eventCategory: advertisingCategory,
    eventAction: advertisingAction,
    eventLabel: advertisingLabel,
    eventValue: advertisingValue,
    event: 'mainEvent',
  };

  const viewBlockEvent = {
    eventCategory: advertisingCategory,
    eventAction: 'Показ',
    eventLabel: advertisingLabel,
    event: 'mainEvent',
  };

  const isLeasing = tab.type === BUSINESS_OFFERS.biznesLeasing;

  const offerTitleClickEvent: EventPayload = isLeasing
    ? {
        eventAction: 'Заголовок|Переход на витрину',
        eventCategory: tabInfo.advertisingCategory,
        eventLabel: tabInfo.generalLink,
        event: 'mainEvent',
      }
    : undefined;

  const buttonClickEvent: EventPayload = isLeasing
    ? {
        eventAction: 'Кнопка Подробнее|Переход на витрину',
        eventCategory: tabInfo.advertisingCategory,
        eventLabel: `${organizationName}|${productName}|${tabInfo.generalLink}`,
        event: 'mainEvent',
      }
    : undefined;

  const utmParams = {
    ...analyticsParams.utmParams,
    aff_sub4: `${advertisingLabel}|${organizationId}|${item._id}`,
    aff_sub5: analyticsParams.testIdVariant
      ? `${advertisingCategory}|${analyticsParams.testIdVariant}`
      : advertisingCategory,
  };

  const { clickLink, displayLink } = decorateLinks({
    displayLink: item.displayLink,
    clickLink: item.clickLink,
    utmParams,
    isPopup,
  });

  const sendViewingEventViaPixel = () => {
    if (displayLink && isAnalyticsParamsLoaded) {
      dispatch(addPixel(displayLink));
    }
  };
  const sendClickEventViaPixel = () => {
    if (clickLink && isAnalyticsParamsLoaded) {
      dispatch(addPixel(clickLink));
    }
  };

  let buttonLabel = '';

  if (isVerticalWidget) {
    if (item.advertising) {
      buttonLabel = isMobile ? item.advertising.buttonMobileText : item.advertising.buttonText;
    } else {
      switch (tab.type) {
        case COMMON_OFFERS.loan: {
          buttonLabel = `${buttonAction} в ${bankName}`;
          break;
        }
        case OTHER_OFFERS.bukmekery: {
          buttonLabel = 'Получить бонус';
          break;
        }
        default: {
          buttonLabel = 'Подробнее';
        }
      }
    }
  } else {
    switch (tab.type) {
      case COMMON_OFFERS.loan:
      case COMMON_OFFERS.mortgage: {
        buttonLabel = 'Перейти на сайт';
        break;
      }
      case COMMON_OFFERS.bankrotstvo: {
        buttonLabel = 'Узнать бесплатно';
        break;
      }
      default: {
        buttonLabel = 'Подробнее';
      }
    }
  }

  return {
    bankName,
    logoSrc,
    isCard,
    offerTitle,
    rating: orgRating,
    reviewCount: orgReviewCount,
    conditions,
    isPartnerCard,
    organization,
    noticeAboutAdvertisement,
    tag,
    buttonLabel,
    clickBlockEvent,
    viewBlockEvent,
    offerTitleClickEvent,
    buttonClickEvent,
    clickLink,
    sendViewingEventViaPixel,
    sendClickEventViaPixel,
    labelFromAdvertising: advertising?.labelText,
  };
};
